import { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import FitScreenIcon from '@mui/icons-material/FitScreen'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import LinkIcon from '@mui/icons-material/Link'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import CloseIcon from '@mui/icons-material/Close'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { PdfPageCanvas, WorkbenchTheme, usePdfDocument, ZOOM_STEPS, nextZoom } from '@standalone-ai/redaction-workbench'
import { pdfjs } from '../../api/pdfjs.js'
import { makeRedactionPorts } from '../../api/redactionPorts.js'
import { tokens } from '../../theme.js'
import {
  DialogTitle,
  Button,
  SecondaryButton,
  MedBodyText,
  MutedText,
  TextField,
  IconOnlyButton,
  ErrorText,
  Spinner,
  Toggle,
  plural
} from '../styles/index.js'
import { folderPath } from '../files/FolderTree.js'
import { useExclusionNoteLimit } from '../../hooks/useLimits.js'
import { formatSize, formatDay } from '../common/text.js'
import { initialPanes, showIn, allExcluded } from './dedupReview.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body
export const DUPLICATE_NOTE = 'Duplicate'

const Wizard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`
const Head = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.background.medium};
`
const Segments = styled.div`
  display: flex;
  gap: 4px;
  width: 120px;
  flex: 0 0 auto;
  & > span {
    flex: 1;
    height: 4px;
    border-radius: 2px;
  }
`
const Strip = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
  padding: 12px 20px;
  background: ${colors.background.light};
  border-bottom: 1px solid ${colors.background.medium};
  overflow-x: auto;
`
const Card = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${p => (p.$shown ? colors.primaryExtraLight : colors.white)};
  border: 1px solid ${p => (p.$shown ? colors.primary : colors.background.medium)};
  &:hover {
    border-color: ${colors.primary};
  }
  &[aria-disabled='true'] {
    cursor: default;
    opacity: 0.6;
  }
`
const PaneLinks = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 2px;
  & > button {
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font: 500 12px/1.4 ${fontFamily};
    color: ${colors.text.muted};
    white-space: nowrap;
  }
  & > button:hover {
    color: ${colors.primary};
    text-decoration: underline;
  }
  & > button[aria-pressed='true'] {
    color: ${colors.primary};
  }
`
const Thumb = styled.span`
  width: 38px;
  height: 48px;
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid ${colors.background.medium};
  background: ${colors.background.light};
  color: ${colors.text.muted};
`
const CardName = styled.div`
  font: 500 14px/1.4 ${fontFamily};
  color: ${colors.text.body};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const Pill = styled.span`
  min-width: 68px;
  text-align: center;
  padding: 3px 10px;
  border-radius: ${tokens.borderRadius.button};
  font: 500 12px/1.4 ${fontFamily};
  white-space: nowrap;
  color: ${colors.text.body};
  border: 1px solid ${p => (p.$keep ? colors.status.ready : colors.error)};
  background:
    linear-gradient(${p => (p.$keep ? colors.status.ready : colors.error)}1A, ${p => (p.$keep ? colors.status.ready : colors.error)}1A),
    ${colors.white};
`
const Decision = styled.div`
  flex: 0 0 148px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  align-self: center;
`
const Panes = styled.div`
  display: flex;
  gap: 16px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px 20px;
  background: ${colors.background.light};
`
const Pane = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.background.medium};
  border-radius: 10px;
  background: ${colors.white};
  overflow: hidden;
`
const PaneHead = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 16px;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border-bottom: 1px solid ${colors.background.medium};
`
const PaneTitle = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font: 400 13px/1.4 ${fontFamily};
  color: ${colors.text.body};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const ToolGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  font: 400 12px/1 ${fontFamily};
  color: ${colors.text.body};
`
const SyncChip = styled.button`
  flex: 0 0 auto;
  width: 116px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 3px 9px;
  border-radius: ${tokens.borderRadius.button};
  font: 500 12px/1.4 ${fontFamily};
  white-space: nowrap;
  border: 1px solid ${p => (p.$on ? colors.primaryExtraLight : colors.background.medium)};
  color: ${p => (p.$on ? colors.primary : colors.text.muted)};
  background: ${p => (p.$on ? colors.primaryExtraLight : colors.background.medium)};
  & > .MuiSvgIcon-root {
    font-size: 14px;
  }
`
const PaneBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  display: flex;
  justify-content: center;
  padding: 12px;
  background: ${colors.background.medium};
`
const PaneFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 12px;
  border-top: 1px solid ${colors.background.medium};
`
const Foot = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-top: 1px solid ${colors.background.medium};
`
const Warn = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding: 0 20px 12px;
  font: 500 13px/1.4 ${fontFamily};
  color: ${colors.status.loadingText};
  & > .MuiSvgIcon-root {
    font-size: 16px;
  }
`

const PAGE_MARGIN_PX = 28

function ComparePane({ side, file, title, pageIndex, scale, minScale, fit, synced, onPages, onPage, onZoom, onFit, onSync, children }) {
  const ports = useMemo(() => makeRedactionPorts(file.id, 'pdf'), [file.id])
  const pdf = usePdfDocument({ pdfjs, loadDocument: ports.loadDocument, lazyPages: true })
  const { pageCount } = pdf
  const bodyRef = useRef(null)
  useEffect(() => {
    const body = bodyRef.current
    if (!pageCount || !body) return undefined
    if (!pdf.pages[0]) return undefined
    const view = pdf.pages[0].getViewport({ scale: 1 })
    const round = (n, cap) => Math.round(Math.max(0.1, Math.min(cap, n)) * 100) / 100
    const measure = () => {
      const width = (body.clientWidth - PAGE_MARGIN_PX) / view.width
      const page = Math.min(width, (body.clientHeight - PAGE_MARGIN_PX) / view.height)
      onPages(side, pageCount, { width: round(width, ZOOM_STEPS[ZOOM_STEPS.length - 1]), page: round(page, 1) })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(body)
    return () => observer.disconnect()
  }, [pageCount])
  const index = Math.min(pageIndex, Math.max(pageCount - 1, 0))
  const { openPage } = pdf
  useEffect(() => {
    openPage(index)
  }, [openPage, pdf.version, index])
  const percent = Math.round(scale * 100)

  return (
    <Pane
      data-testid="compare-pane"
      data-side={side}
      data-file-id={file.id}
      data-file-name={file.name}
      data-page={pageCount ? index + 1 : ''}
      data-fit={fit || ''}>
      <PaneHead>
        <PaneTitle title={title} data-testid="compare-title">
          {title}
        </PaneTitle>
        <ToolGroup role="group" aria-label={`page of ${file.name}`}>
          <IconOnlyButton
            type="button"
            onClick={() => onPage(side, index - 1)}
            disabled={index === 0}
            aria-label="Previous page"
            data-testid="compare-prev"
            style={{ padding: 2 }}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconOnlyButton>
          <span data-testid="compare-page">{pageCount ? `${index + 1} / ${pageCount}` : '—'}</span>
          <IconOnlyButton
            type="button"
            onClick={() => onPage(side, index + 1)}
            disabled={index >= pageCount - 1}
            aria-label="Next page"
            data-testid="compare-next"
            style={{ padding: 2 }}>
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconOnlyButton>
        </ToolGroup>
        <ToolGroup role="group" aria-label={`zoom of ${file.name}`}>
          <IconOnlyButton
            type="button"
            onClick={() => onZoom(side, -1, scale)}
            disabled={scale <= minScale + 0.001}
            aria-label="Zoom out"
            data-testid="compare-zoom-out"
            style={{ padding: 2 }}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconOnlyButton>
          <span data-testid="compare-zoom">{percent} %</span>
          <IconOnlyButton
            type="button"
            onClick={() => onZoom(side, 1, scale)}
            disabled={scale >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
            aria-label="Zoom in"
            data-testid="compare-zoom-in"
            style={{ padding: 2 }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconOnlyButton>
          <IconOnlyButton
            type="button"
            onClick={() => onFit(side, fit === 'page' ? 'width' : 'page')}
            aria-pressed={fit === 'page'}
            data-testid="compare-fit-page"
            aria-label={fit === 'page' ? 'Fit the width' : 'Fit the whole page'}
            title={fit === 'page' ? 'Fit the width' : 'Fit the whole page'}
            style={{ padding: 2, marginLeft: 2, color: fit === 'page' ? colors.primary : undefined }}>
            <FitScreenIcon sx={{ fontSize: 18 }} />
          </IconOnlyButton>
        </ToolGroup>
        <SyncChip
          type="button"
          $on={synced}
          aria-pressed={synced}
          onClick={onSync}
          data-testid="compare-sync"
          title={synced ? 'Paging and zooming move both files together' : 'Each file pages and zooms on its own'}>
          {synced ? <LinkIcon /> : <LinkOffIcon />} {synced ? 'synced' : 'not synced'}
        </SyncChip>
      </PaneHead>
      <PaneBody ref={bodyRef} data-testid="compare-canvas">
        {pdf.loading && !pageCount && <Spinner size={22} />}
        {pdf.error && !pageCount && (
          <ErrorText role="alert" data-testid="compare-error">
            {file.name} could not be opened.
          </ErrorText>
        )}
        {pageCount > 0 && !pdf.pages[index] && <Spinner size={22} />}
        {pdf.pages[index] && <PdfPageCanvas page={pdf.pages[index]} scale={scale} paintAnnotations />}
      </PaneBody>
      <PaneFoot>{children}</PaneFoot>
    </Pane>
  )
}

export function CompareDuplicates({
  group,
  groups,
  index,
  files,
  folders,
  decisions,
  note,
  excludeCount,
  busy,
  error,
  onDecide,
  onNote,
  onBack,
  onSkip,
  onForward,
  onClose
}) {
  const noteMax = useExclusionNoteLimit()
  const byId = useMemo(() => new Map(files.map(f => [f.id, f])), [files])
  const rowOf = fileId => byId.get(fileId) || { id: fileId, name: 'File no longer listed', kind: 'FILE' }
  const [panes, setPanes] = useState(() => initialPanes(group))
  const [pages, setPages] = useState({ left: 0, right: 0 })
  const [pageIndex, setPageIndex] = useState({ left: 0, right: 0 })
  const [fit, setFit] = useState({ left: { width: 1, page: 1 }, right: { width: 1, page: 1 } })
  const [zoom, setZoom] = useState({ left: 'width', right: 'width' })
  const [synced, setSynced] = useState(true)
  useEffect(() => {
    setPanes(initialPanes(group))
  }, [group.referenceFileId])
  useEffect(() => {
    setPageIndex({ left: 0, right: 0 })
  }, [panes.left, panes.right])

  const onPages = (side, count, scales) => {
    setPages(p => (p[side] === count ? p : { ...p, [side]: count }))
    setFit(f => (Math.abs(f[side].width - scales.width) < 0.02 && Math.abs(f[side].page - scales.page) < 0.02 ? f : { ...f, [side]: scales }))
  }
  const scaleOf = side => (typeof zoom[side] === 'number' ? zoom[side] : fit[side][zoom[side]])
  const fitOf = side => (typeof zoom[side] === 'number' ? null : zoom[side])
  const clamp = (side, n) => Math.min(Math.max(n, 0), Math.max((pages[side] || 1) - 1, 0))
  const onPage = (side, n) => setPageIndex(p => (synced ? { left: clamp('left', n), right: clamp('right', n) } : { ...p, [side]: clamp(side, n) }))
  const onZoom = (side, direction, current) =>
    setZoom(z => {
      const stepped = nextZoom(current, direction)
      const below = stepped <= fit[side].page + 0.001 || stepped >= current - 0.001 // the ladder has no lower rung left
      const scale = direction < 0 && below ? 'page' : stepped
      return synced ? { left: scale, right: scale } : { ...z, [side]: scale }
    })
  const onFit = (side, mode) => setZoom(z => (synced ? { left: mode, right: mode } : { ...z, [side]: mode }))
  const show = (side, fileId) => setPanes(p => showIn(p, side, fileId))
  const toggleSync = side =>
    setSynced(on => {
      if (on) return false
      setPageIndex(p => ({ left: clamp('left', p[side]), right: clamp('right', p[side]) }))
      setZoom(z => ({ left: z[side], right: z[side] }))
      return true
    })

  const left = rowOf(panes.left)
  const right = rowOf(panes.right)
  const isReference = fileId => fileId === group.referenceFileId
  const similarity = fileId => group.files.find(gf => gf.fileId === fileId)?.similarity
  const percent = fileId => `${Math.round((similarity(fileId) ?? 0) * 100)} % similar`
  const paneTitle = f => `${f.name} · ${isReference(f.id) ? 'reference' : percent(f.id)}`
  const everyExcluded = allExcluded(group, decisions)
  const meta = f =>
    [
      f.pageCount != null ? plural(f.pageCount, 'page') : null,
      formatSize(f.sizeBytes),
      f.effectiveDate ? formatDay(f.effectiveDate) : null,
      f.folderId ? folderPath(folders, f.folderId) : 'All files'
    ]
      .filter(Boolean)
      .join(' · ')
  const last = index === groups.length - 1

  const paneToggle = fileId => {
    const f = rowOf(fileId)
    const decision = decisions[fileId]
    return (
      <>
        <MutedText>{isReference(fileId) ? 'Reference:' : 'This file:'}</MutedText>
        <Toggle role="group" aria-label={`keep or exclude ${f.name}`}>
          <button type="button" aria-pressed={decision === 'keep'} onClick={() => onDecide(fileId, 'keep')} disabled={busy} data-testid="dedup-keep">
            Keep
          </button>
          <button
            type="button"
            data-tone="danger"
            aria-pressed={decision === 'exclude'}
            onClick={() => onDecide(fileId, 'exclude')}
            disabled={busy}
            data-testid="dedup-exclude">
            Exclude
          </button>
        </Toggle>
      </>
    )
  }

  return (
    <Wizard data-testid="dedup-review">
      <Head>
        <DifferenceOutlinedIcon sx={{ fontSize: 22, color: colors.primary }} />
        <DialogTitle style={{ margin: 0 }}>Duplicates</DialogTitle>
        <MutedText data-testid="dedup-group-title">
          · Group {index + 1} of {groups.length} · {plural(group.files.length, 'similar file')}
        </MutedText>
        {groups.length > 1 && (
          <Segments data-testid="dedup-progress">
            {groups.map((_, i) => (
              <span key={i} style={{ background: i < index ? colors.primaryLight : i === index ? colors.primary : colors.background.medium }} />
            ))}
          </Segments>
        )}
        <MutedText style={{ marginLeft: 'auto' }}>Groups with 2+ files · reference = earliest document date</MutedText>
        <IconOnlyButton
          type="button"
          onClick={onClose}
          disabled={busy}
          title="Close"
          aria-label="Close"
          data-testid="dedup-close"
          style={{ padding: 4 }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconOnlyButton>
      </Head>

      <Strip data-testid="dedup-strip">
        {group.files.map(gf => {
          const f = rowOf(gf.fileId)
          const side = panes.left === gf.fileId ? 'left' : panes.right === gf.fileId ? 'right' : ''
          const decision = decisions[gf.fileId]
          const onClick = e => {
            if (!busy) show(e.shiftKey ? 'left' : 'right', gf.fileId)
          }
          const link = (to, label) => (
            <button
              type="button"
              aria-pressed={side === to}
              disabled={busy}
              onClick={e => {
                e.stopPropagation()
                show(to, gf.fileId)
              }}
              title={`Show in the ${to} pane`}
              data-testid={`dedup-show-${to}`}>
              {label}
            </button>
          )
          return (
            <Card
              key={gf.fileId}
              $shown={!!side}
              onClick={onClick}
              aria-disabled={busy || undefined}
              data-testid="dedup-file"
              data-file-id={gf.fileId}
              data-file-name={f.name}
              data-decision={decision}
              data-pane={side}>
              <Thumb>
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 20 }} />
              </Thumb>
              <div style={{ minWidth: 0, flex: 1 }}>
                <CardName title={f.name}>{f.name}</CardName>
                <MutedText style={{ fontSize: 12 }}>{meta(f)}</MutedText>
                <PaneLinks>
                  {link('left', '◂ left pane')}
                  {link('right', 'right pane ▸')}
                </PaneLinks>
              </div>
              <Decision>
                <div style={{ textAlign: 'right' }}>
                  {isReference(gf.fileId) ? (
                    <div style={{ font: `600 14px/1.3 ${fontFamily}`, color: colors.primary }} data-testid="dedup-reference">
                      Reference
                    </div>
                  ) : (
                    <div style={{ font: `600 14px/1.3 ${fontFamily}`, color: colors.text.body }} data-testid="dedup-similarity">
                      {Math.round(gf.similarity * 100)} %
                    </div>
                  )}
                  <MutedText style={{ fontSize: 11 }}>{isReference(gf.fileId) ? 'earliest date' : 'similar'}</MutedText>
                </div>
                <Pill $keep={decision === 'keep'} data-testid="dedup-decision">
                  {decision === 'keep' ? 'Keep' : 'Exclude'}
                </Pill>
              </Decision>
            </Card>
          )
        })}
      </Strip>

      <WorkbenchTheme tokens={tokens}>
        <Panes>
          <ComparePane
            key={left.id}
            side="left"
            file={left}
            title={paneTitle(left)}
            pageIndex={pageIndex.left}
            scale={scaleOf('left')}
            minScale={fit.left.page}
            fit={fitOf('left')}
            synced={synced}
            onPages={onPages}
            onPage={onPage}
            onZoom={onZoom}
            onFit={onFit}
            onSync={() => toggleSync('left')}>
            {paneToggle(left.id)}
          </ComparePane>
          {right.id && (
            <ComparePane
              key={right.id}
              side="right"
              file={right}
              title={paneTitle(right)}
              pageIndex={pageIndex.right}
              scale={scaleOf('right')}
              minScale={fit.right.page}
              fit={fitOf('right')}
              synced={synced}
              onPages={onPages}
              onPage={onPage}
              onZoom={onZoom}
              onFit={onFit}
              onSync={() => toggleSync('right')}>
              {paneToggle(right.id)}
            </ComparePane>
          )}
        </Panes>
      </WorkbenchTheme>

      <Foot>
        <MedBodyText style={{ flex: '0 0 auto' }}>Exclusion note (optional)</MedBodyText>
        <TextField
          value={note}
          onChange={e => onNote(e.target.value.slice(0, noteMax))}
          placeholder="Leave blank for no note"
          style={{ width: 320 }}
          disabled={busy}
          inputProps={{ 'data-testid': 'dedup-note', maxLength: noteMax, 'aria-label': 'Exclusion note' }}
        />
        <MutedText style={{ flex: '1 1 auto', minWidth: 0 }}>written on every file this review excludes</MutedText>
        {error && (
          <ErrorText role="alert" style={{ margin: 0 }} data-testid="dedup-error">
            {error}
          </ErrorText>
        )}
        <SecondaryButton type="button" onClick={onBack} disabled={index === 0 || busy} data-testid="dedup-back">
          <ArrowBackIcon /> Back
        </SecondaryButton>
        <SecondaryButton type="button" onClick={onSkip} disabled={busy} data-testid="dedup-skip">
          Skip group
        </SecondaryButton>
        <Button type="button" onClick={onForward} loading={busy} data-testid="dedup-apply">
          {excludeCount ? `Exclude ${plural(excludeCount, 'file')}` : last ? 'Finish' : 'Continue'}
        </Button>
      </Foot>
      {everyExcluded && (
        <Warn role="status" data-testid="dedup-all-excluded">
          <WarningAmberOutlinedIcon /> This excludes every copy in this group
        </Warn>
      )}
    </Wizard>
  )
}
